import dayjs from "dayjs";
import { SetStateAction, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MediaSource } from "../../../../../../models/media/MediaSource";
import { ApplicationState } from "../../../../../../store";
import { FileData, useGetTasksQuery } from "../../../../../../store/api/kinesense";
import { DateFormats } from "../../../../../../utilities/dates";
import { UploadUtil, UploadUtilOptions } from "../../../fileUpload/UploadUtil";
import { ImportRequestState } from "../../ImportWizard";
import "./SummaryPage.scss";
import { Link } from "react-router-dom";
import { buildMediaSourceQueryString } from "../../../../../../hooks/useManageViews";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHatWizard, faPlay, faRefresh } from "@fortawesome/free-solid-svg-icons";
import {
    getTaskStepMessage,
    getTaskStepStatusMessage,
    getVideoProcessingTaskStatus,
} from "../../../../../../models/tasks/Task";
import ImportProgressBar, { ImportProgressBarProps } from "./components/ImportProgressBar";
import useHandleFiles from "./hooks/useHandleFiles";
import useHandleLibrary from "./hooks/useHandleLibrary";
import useHandleIntegration from "./hooks/useHandleIntegration";
import useHandleRequestAnalysis from "./hooks/useHandleRequestAnalysis";

export interface SummaryPageProps {
    request: ImportRequestState;
    getUploader: () => UploadUtil | undefined;
    createUploader: (options: UploadUtilOptions) => UploadUtil;
    hideNavButtons: boolean;
    setHideNavButtons: (_: SetStateAction<boolean>) => void;
    resetImportWizard: () => void;
}

export type AnalysisRequestState = "initial" | "uploading" | "transferring" | "requested" | "failed" | "complete";

export type SummaryHandlerReturnArgs = {
    setupFiles: (mediaSource: MediaSource) => Promise<FileData[]>;
    resetFiles: () => void;
    renderSourceSummaryRow: () => JSX.Element;
    renderAdditionalSummaryRows: () => JSX.Element;
    renderProgressBar: () => JSX.Element;
};

export type SummaryHandlerProps = SummaryPageProps & {
    uploadState: AnalysisRequestState;
    setUploadState: (_: React.SetStateAction<AnalysisRequestState>) => void;
};

function selectHandlerHook(request: ImportRequestState): (_: SummaryHandlerProps) => SummaryHandlerReturnArgs {
    switch (request.source.data.type) {
        case "file":
            return useHandleFiles;
        case "library":
            return useHandleLibrary;
        case "integration":
            return useHandleIntegration;
    }
}

function SummaryPage(props: SummaryPageProps) {
    const { general } = useSelector((state: ApplicationState) => state);
    const projectId = general?.activeProjectId;

    const [uploadState, setUploadState] = useState<AnalysisRequestState>("initial");
    const [uploadedMediaSource, setUploadedMediaSource] = useState<MediaSource>(undefined);

    const { data: tasks } = useGetTasksQuery({ projectId }, { skip: !projectId });
    const task = (tasks ?? []).find((t) => t.references.mediaId == uploadedMediaSource?.mediaId);

    const transcodeSteps = task?.steps.filter((s) => s.type == "transcode" || s.type == "link");
    const totalTranscodeWeight = transcodeSteps?.reduce((acc, step) => acc + step.weight, 0) ?? 1;
    const totalTranscodeProgress =
        transcodeSteps?.reduce((acc, step) => acc + step.progress * (step.weight / totalTranscodeWeight), 0) ?? 0;

    const analysisStep = task?.steps?.find((s) => s.type == "analysis");
    const processingStatus = getVideoProcessingTaskStatus(task);

    const processingProgressBar: ImportProgressBarProps | undefined = processingStatus
        ? {
              message: `Processing for display - ${getTaskStepStatusMessage(processingStatus.status)}`,
              progress: totalTranscodeProgress * 100,
              isError: transcodeSteps.some((s) => s.status == "error"),
          }
        : undefined;
    const analysisProgressBar: ImportProgressBarProps | undefined = analysisStep
        ? {
              message: getTaskStepMessage(analysisStep),
              progress: (analysisStep?.progress ?? 0) * 100,
              isError: analysisStep.status == "error",
          }
        : undefined;

    const {
        renderProgressBar,
        renderAdditionalSummaryRows: renderRowElements,
        renderSourceSummaryRow: renderSourceRow,
        setupFiles,
        resetFiles,
    } = selectHandlerHook(props.request)({ ...props, uploadState, setUploadState });

    const { handleRequestAnalysis } = useHandleRequestAnalysis({
        ...props,
        setUploadState,
        setUploadedMediaSource,
        setupFiles,
        resetFiles,
    });

    function renderUploadState() {
        switch (uploadState) {
            case "failed":
                return (
                    <div className="gap-2 d-flex align-items-center">
                        <Button
                            variant="secondary"
                            disabled={props.hideNavButtons}
                            className="gap-2 d-flex align-items-center"
                            onClick={handleRequestAnalysis}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            Try again
                        </Button>

                        <Button
                            variant="primary"
                            disabled={props.hideNavButtons}
                            className="gap-2 d-flex align-items-center"
                            onClick={props.resetImportWizard}
                        >
                            <FontAwesomeIcon icon={faHatWizard} />
                            Import a different file
                        </Button>
                    </div>
                );
            case "initial":
                return (
                    <Button disabled={props.hideNavButtons} onClick={handleRequestAnalysis}>
                        Analyse Video
                    </Button>
                );
            case "transferring":
                return <Button disabled={true}>Transferring file...</Button>;
            case "uploading":
                return (
                    <div className="gap-2 flex-column d-flex w-100 align-items-center">
                        {renderProgressBar()}
                        <p className="m-0 fw-bold">Closing this page during file upload will cancel the import.</p>
                    </div>
                );
            case "requested":
                return (
                    <div className="gap-2 flex-column d-flex w-100 align-items-center">
                        {renderProgressBar()}

                        {processingProgressBar && <ImportProgressBar {...processingProgressBar} />}
                        {analysisProgressBar && <ImportProgressBar {...analysisProgressBar} />}

                        <div className="gap-2 mt-4 d-flex align-items-center">
                            <Button
                                style={{ width: "max-content" }}
                                className="gap-2 btn btn-secondary d-flex align-items-center"
                                onClick={props.resetImportWizard}
                            >
                                <FontAwesomeIcon icon={faHatWizard} />
                                Start another import
                            </Button>

                            <span className="text-muted">or</span>

                            <Link
                                style={{ width: "max-content" }}
                                className="gap-2 btn btn-primary d-flex align-items-center"
                                to={`/review?${buildMediaSourceQueryString(
                                    general.activeProjectId,
                                    uploadedMediaSource?.mediaId,
                                )}`}
                            >
                                <FontAwesomeIcon icon={faPlay} />
                                {task?.isComplete ? "View in Review" : "View import progress"}
                            </Link>
                        </div>
                    </div>
                );
        }
    }

    return (
        <div>
            <Table className="summary-table">
                <tbody>
                    <tr>
                        <td>Name</td>
                        <td>{props.request.source.data?.name}</td>
                    </tr>
                    <tr>
                        <td>Start Time</td>
                        <td>
                            {dayjs(props.request.source.data?.startTime).format(
                                DateFormats.yearMonthDayWithTimeSeconds,
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Algorithm</td>
                        <td>{props.request.algorithm.data?.displayName}</td>
                    </tr>

                    {renderSourceRow()}

                    {renderRowElements()}
                </tbody>
            </Table>

            <div className="mt-3 d-flex justify-content-center">{renderUploadState()}</div>
        </div>
    );
}

export default SummaryPage;
